import {useAuth} from "../auth/AuthHook";
import {useLocation} from "react-router-dom";
import {useTranslation} from "react-i18next";
import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {ContextualEvent, useSupportCase} from "../support/CaseHook";
import {CaseInfo, CaseMetaInfo} from "../support/model";
import {SelectableLink} from "../components/SelectableLink";
import {EditableBox} from "../components/EditableBox";
import {ConfirmationDialog} from "../components/ConfirmationDialog";
import {CaseRenamingEndEvent} from "../events/ProgressEvents";

export function RecentCases() {
    const {t} = useTranslation()
    const {isLoggedIn} = useAuth()
    const {caseId, fetchData, updateSupportCase, setCaseStateMap, subscribeToEvents} = useSupportCase()
    const [loading, setLoading] = useState<boolean>(false);
    const [cases, setCases] = useState<CaseInfo[]>([])
    const [openMenuCaseId, setOpenMenuCaseId] = useState<string | null>(null);
    const [renamingCaseId, setRenamingCaseId] = useState<string | null>(null);
    const [deletingCaseId, setDeletingCaseId] = useState<string | null>(null);
    const menuRef = useRef<HTMLDivElement>(null);
    const listContainerRef = useRef<HTMLDivElement>(null);
    const firstItemRef = useRef<HTMLLIElement>(null);
    const [visibleCount, setVisibleCount] = useState<number>(1);
    const location = useLocation();


    const doFetchHistory = useCallback(async () => {
        try {
            setLoading(true);
            const response = await fetchData(`api/cases/`, "GET")
            if (response.ok || response.status <= 299) {
                setCases(await response.json())
            }
        } catch (e) {
            // ignore
        }
        setLoading(false);
    }, [fetchData])

    const fetchHistory = useCallback(async () => {
        if (isLoggedIn()) {
            await doFetchHistory();
        } else {
            setCases((prevCases) => {
                if (prevCases.length > 0) {
                    return []
                }
                return prevCases;
            })
        }
    }, [isLoggedIn, doFetchHistory])

    useEffect(() => {
        fetchHistory()
    }, [fetchHistory, location.pathname])

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
                setOpenMenuCaseId(null);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    useEffect(() => {
        const handleReceivedRename = async (event: ContextualEvent) => {
            console.log(`Received event: ${event}`);
            if (event.event.activity === "renaming" && event.event.position === "end") {
                const renameEndEvent = event.event as CaseRenamingEndEvent
                const title = renameEndEvent.title
                setCases(prev => prev.map(c => c.case_id === event.case_id ? {...c, title} : c));
            }
        }
        return subscribeToEvents(handleReceivedRename);
    }, [subscribeToEvents]);

    const handleRenameCompletion = useCallback(async (targetCaseId: string, updatedTitle: string) => {
        try {
            const metaInfo: CaseMetaInfo = {
                ...(cases.find(c => c.case_id === targetCaseId) as CaseMetaInfo),
                title: updatedTitle
            };
            const resp = await fetchData("api/cases/" + targetCaseId, "POST", metaInfo);
            const meta = await resp.json();
            if (meta != null) {
                setCases(prev => prev.map(c => c.case_id === targetCaseId ? {...c, ...meta} : c));
                setCaseStateMap(prev => {
                    const newMap = new Map(prev);
                    const existing = newMap.get(targetCaseId);
                    if (existing?.supportCase) {
                        newMap.set(targetCaseId, {
                            ...existing,
                            supportCase: {
                                ...existing.supportCase,
                                title: updatedTitle
                            }
                        });
                    }
                    return newMap;
                });
            }
        } catch (e) {
            // ignore
        }
        setRenamingCaseId(null);
    }, [fetchData, cases, setCaseStateMap]);

    const handleDelete = useCallback(async (targetCaseId: string) => {
        try {
            await fetchData("api/cases/" + targetCaseId, "DELETE");
            setCases(prev => prev.filter(c => c.case_id !== targetCaseId));
        } catch (e) {
            // ignore
        }
        setOpenMenuCaseId(null);
    }, [fetchData]);

    const PADDING_FOR_LOCAL_MENU = 2;

    useEffect(() => {
        const containerHeight = listContainerRef.current?.clientHeight;
        const firstItemHeight = firstItemRef.current?.getBoundingClientRect().height;
        if (containerHeight == null || firstItemHeight == null || firstItemHeight === 0) {
            return
        }
        const count = Math.max(1, Math.floor(containerHeight / firstItemHeight) - PADDING_FOR_LOCAL_MENU);
        if (count !== visibleCount) {
            setVisibleCount(count);
        }
    }, [visibleCount, firstItemRef, listContainerRef, cases.length]);

    const containerHeight = listContainerRef.current?.clientHeight ?? 0;
    const itemHeight = firstItemRef.current?.getBoundingClientRect().height ?? 0;
    if (containerHeight > 0 && itemHeight > 0) {
        const count = Math.max(1, Math.floor(containerHeight / itemHeight) - PADDING_FOR_LOCAL_MENU);
        if (count !== visibleCount) {
            setVisibleCount(count);
        }
    }

    const sortedCases = useMemo(() =>
        [...cases].sort((a, b) => new Date(b.creation_time ?? 0).getTime() - new Date(a.creation_time ?? 0).getTime()),
        [cases]
    );
    const recentCases = sortedCases.slice(0, visibleCount);



    return (
        <nav className="flex flex-col justify-start px-1 h-full overflow-hidden">
            <div className="flex flex-row text-left font-bold text-sm px-3 text-neutral-500 dark:text-neutral-500 justify-between pb-2">
                <span>{t("Cases")}</span>
            </div>
            <div ref={listContainerRef} className="flex-1 overflow-visible">
            {cases.length === 0 ? (
                <p className={"px-3"}>{t("No recent cases")}</p>
            ) : (
                <>
                    <ul className="list-inside list-none px-1" id={"recent-cases-list"}>
                        {recentCases.map((recentCase, index) => (
                            <li key={recentCase.case_id} ref={index === 0 ? firstItemRef : undefined} className={"w-full relative group"}>
                                {renamingCaseId === recentCase.case_id ? (
                                    <EditableBox
                                        initialValue={recentCase.title}
                                        onAccept={(updatedText) => handleRenameCompletion(recentCase.case_id!, updatedText)}
                                        onCancel={() => setRenamingCaseId(null)}
                                    />
                                ) : (
                                    <div className="flex items-center w-full">
                                        <div className="flex-1 min-w-0 w-full">
                                            <SelectableLink
                                                to={`/cases/${recentCase.case_id}?org_id=${recentCase.org_id}`}
                                                className="flex flex-row w-full rounded-xl px-2 py-1 justify-between"
                                                hasBorder={false}
                                                isSelected={caseId === recentCase.case_id}
                                                title={recentCase.title?.replace(/\n/g, " ") ?? "Untitled"}
                                            >
                                                <div className={"text-nowrap w-fit overflow-hidden mr-2"}>{recentCase.title}</div>
                                                <button
                                                    className="opacity-0 group-hover:opacity-100 text-neutral-400 hover:text-white px-1 text-lg leading-none"
                                                    onClick={(e) => {
                                                        console.log("clicked menu button")
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        setOpenMenuCaseId(prev => prev === recentCase.case_id ? null : recentCase.case_id);
                                                    }}
                                                    aria-label={t("More options")}
                                                >
                                                    ⋯
                                                </button>
                                            </SelectableLink>
                                        </div>
                                        <div className="relative" ref={openMenuCaseId === recentCase.case_id ? menuRef : undefined}>
                                            {openMenuCaseId === recentCase.case_id && (
                                                <div className="absolute right-0 top-full z-50 mt-1 w-32 bg-neutral-800 border border-neutral-600 rounded-lg shadow-lg overflow-hidden">
                                                    <button
                                                        className="w-full text-left px-3 py-2 text-sm text-white hover:bg-neutral-700"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setOpenMenuCaseId(null);
                                                            setRenamingCaseId(recentCase.case_id);
                                                        }}
                                                    >
                                                        {t("Rename")}
                                                    </button>
                                                    <button
                                                        className="w-full text-left px-3 py-2 text-sm hover:bg-neutral-700"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setOpenMenuCaseId(null);
                                                            setDeletingCaseId(recentCase.case_id!);
                                                        }}
                                                    >
                                                        {t("Delete")}
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </li>
                        ))}
                    </ul>
                </>

            )}
            </div>
            <ConfirmationDialog
                isOpen={deletingCaseId !== null}
                onClose={() => setDeletingCaseId(null)}
                content={
                    <>
                        <h2 className="text-lg font-semibold mb-2 text-neutral-900 dark:text-neutral-100">
                            {t("Delete case")}
                        </h2>
                        <p className="text-sm text-neutral-600 dark:text-neutral-400 mb-6">
                            {t("Are you sure you want to delete this case? This action cannot be undone.")}
                        </p>
                    </>
                }
                cancelButton={
                    <button
                        className="px-4 py-2 text-sm font-medium rounded-md bg-neutral-200 dark:bg-neutral-700 text-neutral-800 dark:text-neutral-200 hover:bg-neutral-300 dark:hover:bg-neutral-600"
                        onClick={() => setDeletingCaseId(null)}
                    >
                        {t("Cancel")}
                    </button>
                }
                submitButton={
                    <button
                        className="px-4 py-2 text-sm font-medium rounded-md bg-red-500 text-white hover:bg-red-700"
                        onClick={async () => {
                            await handleDelete(deletingCaseId!);
                            setDeletingCaseId(null);
                        }}
                    >
                        {t("Delete")}
                    </button>
                }
            />
        </nav>
    )


}
